import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect, useState, useCallback } from "react";
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdTableChart,
  MdAddBox,
  MdIndeterminateCheckBox,
  MdCallMerge,
  MdCallSplit,
  MdSmartButton,
} from "react-icons/md";
import "./TiptapTableEditor.css";
import { InlineButton, InlineButtonAttrs } from "./extensions";
import { InlineButtonModal } from "./InlineButtonModal";

interface TiptapTableEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
}

export const TiptapTableEditor = ({ value, onChange }: TiptapTableEditorProps) => {
  const [buttonModalOpen, setButtonModalOpen] = useState(false);
  const [editingButton, setEditingButton] = useState<{ attrs: InlineButtonAttrs; pos: number } | null>(null);

  const handleEditButton = useCallback((attrs: InlineButtonAttrs, pos: number) => {
    setEditingButton({ attrs, pos });
    setButtonModalOpen(true);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TableKit.configure({
        table: { resizable: true },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      InlineButton.configure({
        onEditButton: handleEditButton,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const insertTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const addRowBefore = () => editor.chain().focus().addRowBefore().run();
  const addRowAfter = () => editor.chain().focus().addRowAfter().run();
  const deleteRow = () => editor.chain().focus().deleteRow().run();
  const addColumnBefore = () => editor.chain().focus().addColumnBefore().run();
  const addColumnAfter = () => editor.chain().focus().addColumnAfter().run();
  const deleteColumn = () => editor.chain().focus().deleteColumn().run();
  const mergeCells = () => editor.chain().focus().mergeCells().run();
  const splitCell = () => editor.chain().focus().splitCell().run();
  const deleteTable = () => editor.chain().focus().deleteTable().run();

  const openButtonModal = () => {
    setEditingButton(null);
    setButtonModalOpen(true);
  };

  const handleButtonModalSubmit = (attrs: InlineButtonAttrs) => {
    if (editingButton) {
      // Update existing button
      editor.commands.updateInlineButton(editingButton.pos, attrs);
    } else {
      // Insert new button
      editor.commands.insertInlineButton(attrs);
    }
    setButtonModalOpen(false);
    setEditingButton(null);
  };

  const handleButtonModalClose = () => {
    setButtonModalOpen(false);
    setEditingButton(null);
  };

  return (
    <div className="tiptap-table-editor rounded-lg border border-slate-200 dark:border-slate-600">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 p-2 dark:border-slate-600 dark:bg-slate-800">
        {/* Text formatting */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700 ${
            editor.isActive("bold") ? "bg-slate-200 dark:bg-slate-700" : ""
          }`}
          title="Pogrubienie"
        >
          <MdFormatBold size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700 ${
            editor.isActive("italic") ? "bg-slate-200 dark:bg-slate-700" : ""
          }`}
          title="Kursywa"
        >
          <MdFormatItalic size={18} />
        </button>

        <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Text alignment */}
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={`rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700 ${
            editor.isActive({ textAlign: "left" }) ? "bg-slate-200 dark:bg-slate-700" : ""
          }`}
          title="Wyrównaj do lewej"
        >
          <MdFormatAlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={`rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700 ${
            editor.isActive({ textAlign: "center" }) ? "bg-slate-200 dark:bg-slate-700" : ""
          }`}
          title="Wyśrodkuj"
        >
          <MdFormatAlignCenter size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={`rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700 ${
            editor.isActive({ textAlign: "right" }) ? "bg-slate-200 dark:bg-slate-700" : ""
          }`}
          title="Wyrównaj do prawej"
        >
          <MdFormatAlignRight size={18} />
        </button>

        <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Table controls */}
        <button
          type="button"
          onClick={insertTable}
          className="rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Wstaw tabelę"
        >
          <MdTableChart size={18} />
        </button>

        <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Row controls */}
        <button
          type="button"
          onClick={addRowBefore}
          className="flex items-center rounded p-1.5 text-xs transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Dodaj wiersz powyżej"
        >
          <MdAddBox size={16} />
          <span className="ml-0.5">Wiersz ↑</span>
        </button>
        <button
          type="button"
          onClick={addRowAfter}
          className="flex items-center rounded p-1.5 text-xs transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Dodaj wiersz poniżej"
        >
          <MdAddBox size={16} />
          <span className="ml-0.5">Wiersz ↓</span>
        </button>
        <button
          type="button"
          onClick={deleteRow}
          className="flex items-center rounded p-1.5 text-xs text-red-600 transition-colors hover:bg-red-100 dark:hover:bg-red-900/30"
          title="Usuń wiersz"
        >
          <MdIndeterminateCheckBox size={16} />
          <span className="ml-0.5">Wiersz</span>
        </button>

        <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Column controls */}
        <button
          type="button"
          onClick={addColumnBefore}
          className="flex items-center rounded p-1.5 text-xs transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Dodaj kolumnę przed"
        >
          <MdAddBox size={16} />
          <span className="ml-0.5">Kol ←</span>
        </button>
        <button
          type="button"
          onClick={addColumnAfter}
          className="flex items-center rounded p-1.5 text-xs transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Dodaj kolumnę po"
        >
          <MdAddBox size={16} />
          <span className="ml-0.5">Kol →</span>
        </button>
        <button
          type="button"
          onClick={deleteColumn}
          className="flex items-center rounded p-1.5 text-xs text-red-600 transition-colors hover:bg-red-100 dark:hover:bg-red-900/30"
          title="Usuń kolumnę"
        >
          <MdIndeterminateCheckBox size={16} />
          <span className="ml-0.5">Kol</span>
        </button>

        <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Merge/Split */}
        <button
          type="button"
          onClick={mergeCells}
          className="rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Scal komórki"
        >
          <MdCallMerge size={18} />
        </button>
        <button
          type="button"
          onClick={splitCell}
          className="rounded p-1.5 transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Rozdziel komórkę"
        >
          <MdCallSplit size={18} />
        </button>

        <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Inline button */}
        <button
          type="button"
          onClick={openButtonModal}
          className="flex items-center gap-1 rounded p-1.5 text-xs transition-colors hover:bg-slate-200 dark:hover:bg-slate-700"
          title="Dodaj przycisk"
        >
          <MdSmartButton size={16} />
          <span>Przycisk</span>
        </button>

        <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Delete table */}
        <button
          type="button"
          onClick={deleteTable}
          className="rounded p-1.5 text-xs text-red-600 transition-colors hover:bg-red-100 dark:hover:bg-red-900/30"
          title="Usuń tabelę"
        >
          Usuń tabelę
        </button>
      </div>

      {/* Editor content */}
      <div className="tiptap-editor-content min-h-[200px] p-4">
        <EditorContent editor={editor} />
      </div>

      {/* Inline button modal */}
      <InlineButtonModal
        isOpen={buttonModalOpen}
        onClose={handleButtonModalClose}
        onSubmit={handleButtonModalSubmit}
        defaultValues={editingButton?.attrs}
        isEdit={!!editingButton}
      />
    </div>
  );
};
